import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Collapse } from 'reactstrap';

import { RESOURCE_TYPES } from '../../constants';
import {
	getCompletionRequirements,
	getCompletionRequirementsLRSActionMap,
} from '../../utils';
import CompletionTracker from '../CompletionTracker';

import './CompletionTree.scss';

const propTypes = {

	/**
	 * The resource at the current node in the completion tree.
	 */
	resource: PropTypes.shape({

		/**
		 * The resource ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource slug.
		 */
		slug: PropTypes.string.isRequired,

		/**
		 * The resource title.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.string.isRequired,

		/**
		 * The nested child resources (if any) for the resource.
		 */
		children: PropTypes.node,

	}),

	/**
	 * The parent's subcondition for the resource at the current node in the completion tree.
	 */
	parentSubcondition: PropTypes.shape({

		active: PropTypes.boolean,

		action: PropTypes.string,

		object_type: PropTypes.string,

		object_id: PropTypes.string,

		object_title: PropTypes.string,

		parent_object_id: PropTypes.string,

		parent_object_title: PropTypes.string,

		rule_id: PropTypes.string,

		tracker: PropTypes.shape({

			status: PropTypes.oneOf(['not-started', 'in-progress', 'complete']),

		}),

	}).isRequired,

	/**
	 * A map of ID -> resource data for all resources in scope.
	 */
	resourcesByID: PropTypes.objectOf(PropTypes.shape({

		/**
		 * The resource ID.
		 */
		id: PropTypes.string.isRequired,

	})).isRequired,

	/**
	 * A map of object ID -> rule data for all rules in scope.
	 */
	rulesByObjectID: PropTypes.objectOf(PropTypes.shape({

		/**
		 * The rule ID.
		 */
		id: PropTypes.string.isRequired,

	})).isRequired,

	/**
	 * A mapping of rule ID -> completion progress percentage for all rules in scope.
	 */
	progressByRuleID: PropTypes.objectOf(PropTypes.number).isRequired,

	/**
	 * A utility function to build the link to the resource for the scope.
	 */
	buildResourceLink: PropTypes.func.isRequired,

};

const CompletionTree = (props) => {

	const {
		parentSubcondition,
		resourcesByID,
		rulesByObjectID,
		progressByRuleID,
		buildResourceLink,
	} = props;

	const intl = useIntl();

	// Find the nested completion tracking rule (if any) for the current subcondition in the tree
	let rule = rulesByObjectID[parentSubcondition.object_id];

	// Avoid infinite recursion when the object IDs are the same for
	// parent rule and child subcondition
	// NOTE: This happens in the case of leaf content,
	// i.e. "viewed:slide", "marked-complete:video"
	if (rule && rule.id === parentSubcondition.rule_id) {
		rule = null;
	}

	// Keep track of toggle state (visible/hidden) for the children completion requirements
	const [showChildren, setShowChildren] = useState(!rule);

	// Get the list of completion requirements for the current node in the tree
	const requiredSubconditions = getCompletionRequirements(rule, resourcesByID);

	// Get the mapping of LRS object types to meta-data used
	// for rendering the current node in the tree
	const requirementActionMap = getCompletionRequirementsLRSActionMap(intl);

	// Get the completion tracker status (if any) for the current node in the tree
	const status =
		(rule && rule.tracker && rule.tracker.status) ||
		(parentSubcondition && parentSubcondition.tracker && parentSubcondition.tracker.status);

	// Format requirement description from subcondition data
	const objectData = requirementActionMap[parentSubcondition.object_type];
	const objectActionData = objectData && objectData.actions[parentSubcondition.action];

	// TODO: Pages too
	const subconditionWithLinks = Object.assign({}, parentSubcondition, {
		object_link: buildResourceLink(parentSubcondition.object_id),
	});

	const actionDescription = objectActionData &&
		objectActionData.display(subconditionWithLinks);

	const actionParentDescription = parentSubcondition.parent_object_id &&
		objectActionData &&
		objectActionData.parentDisplay &&
		objectActionData.parentDisplay(subconditionWithLinks);

	// Check if this is a subcondition of the root rule for the scope
	const isRootSubcondition =
		parentSubcondition.object_type === RESOURCE_TYPES.PAGE;

	return (
		<div
			className={`
				tc-completion-tree-node
				${isRootSubcondition ? 'is-root' : ''}
			`}>
			<div
				className={`d-flex ${requiredSubconditions ? 'collapsible' : ''}
					${showChildren ? 'open' : 'closed'}`}
				onClick={() => setShowChildren((prev) => !prev)}>
				<div className="tc-completion-tree-node-item">
					<CompletionTracker status={status} />
					<span className="d-flex align-items-center ms-2">
						<span
							className={`resource-title ${parentSubcondition.object_type}-title`}>
							{isRootSubcondition && parentSubcondition.object_title}
							{!isRootSubcondition &&
								<Fragment>
									{actionDescription}
									{' '}
									{actionParentDescription}
								</Fragment>
							}
						</span>
						<span className="toggle-icon">
							<i className="fas fa-chevron-down" />
						</span>
					</span>
				</div>
			</div>
			{requiredSubconditions &&
				<Collapse
					className="tc-completion-tree-node-subconditions"
					isOpen={showChildren}>
					{requiredSubconditions.map((subcondition) => (
						<CompletionTree
							key={subcondition.id}
							parentSubcondition={subcondition}
							resourcesByID={resourcesByID}
							rulesByObjectID={rulesByObjectID}
							progressByRuleID={progressByRuleID}
							buildResourceLink={buildResourceLink} />
					))}
				</Collapse>
			}
		</div>
	);

};

CompletionTree.propTypes = propTypes;

export default CompletionTree;
